import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs'; // Add dayjs import
import 'dayjs/locale/en-gb'; // Add import for date formatting
import {
    Box,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    Typography,
    Tooltip,
    CircularProgress, // Add CircularProgress to imports
    Grid
} from '@mui/material';
import { AuthData } from "auth/AuthWarpper";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import './TranslationNotification.css';
import Layout from 'components/structure/Layout';
import TranslationNotificationService from 'services/TranslationNotificationService';
import { SnackbarContext } from "../../../context/snackBarContext";
import { useTranslation } from 'react-i18next';
import SurityModal from 'utils/SurityModal/SurityModal'; // Add import for SurityModal
import WarningIcon from '../../../assets/Warning.svg';
import SuccessIcon from '../../../assets/successIcon.svg';

// Move preloadImage function before component
const preloadImage = (src) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
};

// Add cache object outside component
const iconCache = {
    warning: null,
    success: null
};

const TranslationNotification = () => {
    const { t } = useTranslation(); // Add this line near other hooks 
    const { user } = AuthData();
    
    // Calculate default dates (3 months range)
    const defaultToDate = dayjs(); // current date
    const defaultFromDate = defaultToDate.subtract(3, 'month'); // 3 months before

    // Initialize state with default dates
    const [fromDate, setFromDate] = useState(defaultFromDate);
    const [toDate, setToDate] = useState(defaultToDate);
    const [notificationFromDate, setNotificationFromDate] = useState(null);
    const [notificationToDate, setNotificationToDate] = useState(null);
    const [searchInput, setSearchInput] = useState('');
    const [countries, setCountries] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [selectedItems, setSelectedItems] = useState({});
    const [isLoading, setIsLoading] = useState(false); // Add loading state
    const [openSurityModal, setOpenSurityModal] = useState(false); // Add state for modal
    const [openSuccessModal, setOpenSuccessModal] = useState(false); // Add new state for success modal
    const [bulkSelections, setBulkSelections] = useState({}); // Add new state for bulk selections
    const [selectAllStates, setSelectAllStates] = useState({}); // Add new state for select all
    const {
        severity,
        message,
        openAlert,
        setOpenAlert,
        setSeverity,
        setMessage,
    } = React.useContext(SnackbarContext);

    const moduleName = "Manage Operations";
    const functionalityName = "Send Notification for Translation";

    const read_only_user = user?.accessibleModules
    ?.find(module => module.module_name === moduleName)
    ?.accessible_functionalities
    ?.find(func => func.functionality_name === functionalityName)
    ?.read_only;

    // Add these date validation handlers after your existing state declarations
    const handleFromDateChange = (newValue) => {
        // If new fromDate is after current toDate, update toDate
        if (toDate && newValue && newValue.isAfter(toDate)) {
            setToDate(newValue);
        }
        setFromDate(newValue);
    };

    const handleToDateChange = (newValue) => {
        // If new toDate is before current fromDate, update fromDate
        if (fromDate && newValue && newValue.isBefore(fromDate)) {
            setFromDate(newValue);
        }
        setToDate(newValue);
    };

    // Add new handlers for notification dates
    const handleNotificationFromDateChange = (newValue) => {
        // If new notificationFromDate is after current notificationToDate, update notificationToDate
        if (notificationToDate && newValue && newValue.isAfter(notificationToDate)) {
            setNotificationToDate(newValue);
        }
        setNotificationFromDate(newValue);
    };

    const handleNotificationToDateChange = (newValue) => {
        // If new notificationToDate is before current notificationFromDate, update notificationFromDate
        if (notificationFromDate && newValue && newValue.isBefore(notificationFromDate)) {
            setNotificationFromDate(newValue);
        }
        setNotificationToDate(newValue);
    };

    useEffect(() => {
        const loadIcons = async () => {
            try {
                const [warningIcon, successIcon] = await Promise.all([
                    preloadImage(WarningIcon),
                    preloadImage(SuccessIcon)
                ]);
                iconCache.warning = warningIcon;
                iconCache.success = successIcon;
            } catch (error) {
                console.error('Error preloading icons:', error);
            }
        };

        loadIcons();
        fetchCountries();
    }, []);

    useEffect(() => {
        setBulkSelections({}); // Reset bulk selections when data changes
    }, [tableData]);

    useEffect(() => {
        if (fromDate && toDate) {
            fetchTranslations();
        }
    }, [fromDate, toDate, notificationFromDate, notificationToDate, searchInput]); // Update effect dependencies

    const fetchCountries = async () => {
        try {
            const response = await TranslationNotificationService.getCountries();
            console.log('Countries response:', response);

            setCountries(response?.response || []);
            fetchTranslations();
        } catch (error) {
            // Handle error
        }
    };

    // Update fetchTranslations function
    const fetchTranslations = async () => {
        try {
            const response = await TranslationNotificationService.getTranslationModifications({
                modificationStartDate: fromDate.format('YYYY-MM-DD'),
                modificationEndDate: toDate.format('YYYY-MM-DD'),
                notificationStartDate: notificationFromDate ? notificationFromDate.format('YYYY-MM-DD') : null,
                notificationEndDate: notificationToDate ? notificationToDate.format('YYYY-MM-DD') : null,
                keyword: searchInput || null
            });

            console.log('Translations response:', response);
            setTableData(Array.isArray(response?.response) ? response.response : []);
        } catch (error) {
            console.error('Error fetching translations:', error);
            setTableData([]);
        }
    };

    const handleCheckboxChange = (translationHistoryId, countryCode) => {
        setSelectedItems(prev => {
            const currentValue = prev[translationHistoryId]?.[countryCode];
            
            // Toggle the selection
            return {
                ...prev,
                [translationHistoryId]: {
                    ...prev[translationHistoryId],
                    [countryCode]: !currentValue
                }
            };
        });
    };

    const handleCheckboxClick = (row, country, isDisabled) => {
        console.log('Checkbox clicked:', row, country, isDisabled);
        if (isDisabled) {
            // Show error message using existing SnackbarContext
            setMessage(t("This record is not applicable to the selected market"));
            setSeverity("error");
            setOpenAlert(true);
            return;
        }
        // If not disabled, proceed with normal checkbox change
        handleCheckboxChange(row.translationHistoryId, country.countryCode);
    };

    const handleBulkSelection = (countryCode) => {
        const isCurrentlySelected = bulkSelections[countryCode];
        
        // Update bulk selection state
        setBulkSelections(prev => ({
            ...prev,
            [countryCode]: !isCurrentlySelected
        }));

        // Update individual selections
        setSelectedItems(prev => {
            const newSelections = { ...prev };
            
            tableData.forEach(row => {
                // Skip if endMarket is not present for this country
                if (!row.endMarkets[countryCode]) {
                    return;
                }

                if (!newSelections[row.translationHistoryId]) {
                    newSelections[row.translationHistoryId] = {};
                }
                newSelections[row.translationHistoryId][countryCode] = !isCurrentlySelected;
            });
            
            return newSelections;
        });
    };

    // Add this new function to check if any checkboxes in a row are enabled
    const hasEnabledCheckboxes = (row) => {
        return countries.some(country => row.endMarkets[country.countryCode]);
    };

    // Modify the handleSelectAllForRow function
    const handleSelectAllForRow = (row) => {
        if (!hasEnabledCheckboxes(row)) return; // Don't proceed if all checkboxes are disabled

        setSelectedItems(prev => {
            const newSelections = { ...prev };
            const translationHistoryId = row.translationHistoryId;
            
            // Get current selection state for enabled checkboxes only
            const isAllSelected = countries.every(country => 
                !row.endMarkets[country.countryCode] || // Skip disabled checkboxes
                prev[translationHistoryId]?.[country.countryCode]
            );

            // Toggle selection for all available countries
            countries.forEach(country => {
                if (row.endMarkets[country.countryCode]) {
                    if (!newSelections[translationHistoryId]) {
                        newSelections[translationHistoryId] = {};
                    }
                    newSelections[translationHistoryId][country.countryCode] = !isAllSelected;
                }
            });

            return newSelections;
        });
    };

    // Add bulk select all handler
    const handleBulkSelectAll = () => {
        const isAllSelected = tableData.every(row => 
            countries.every(country => 
                !row.endMarkets[country.countryCode] || // Skip disabled checkboxes
                selectedItems[row.translationHistoryId]?.[country.countryCode]
            )
        );

        setSelectedItems(prev => {
            const newSelections = { ...prev };
            tableData.forEach(row => {
                countries.forEach(country => {
                    if (row.endMarkets[country.countryCode]) {
                        if (!newSelections[row.translationHistoryId]) {
                            newSelections[row.translationHistoryId] = {};
                        }
                        newSelections[row.translationHistoryId][country.countryCode] = !isAllSelected;
                    }
                });
            });
            return newSelections;
        });
    };

    const handleSendNotification = () => {
      
        const selec = Object.values(selectedItems).reduce((acc, countries) => {
            return acc + Object.values(countries).filter(isSelected => isSelected).length;
        }, 0);
        console.log('Selected items count:', selec); // Log the count of selected items
        if (selec === 0) {
            setMessage(t("Please select at least one end market to send notification"));
            setSeverity("error");
            setOpenAlert(true);
            return;
        }
        setOpenSurityModal(true); // Modify handleSendNotification to show modal first
    };

    const handleConfirmSend = async () => {
        setOpenSurityModal(false);
        try {
            setIsLoading(true);
            const notifications = Object.entries(selectedItems).map(([translationHistoryId, countries]) => ({
                translationHistoryId,
                countryCodes: Object.entries(countries)
                    .filter(([_, isSelected]) => isSelected)
                    .map(([countryCode]) => countryCode)
            })).filter(item => item.countryCodes.length > 0);
            
            await TranslationNotificationService.sendNotifications(notifications);
            setSelectedItems({});
            fetchTranslations();
            // Show success modal instead of snackbar
            setOpenSuccessModal(true);
        } catch (error) {
            console.error('Error sending notifications:', error);
            setMessage(error?.response?.data?.message || error?.message);
            setOpenAlert(true);
            setSeverity("error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSuccessModalClose = () => {
        setOpenSuccessModal(false);
    };

    return (
        <Layout title={t('Agri360 ESG Portal')} style={{ marginBottom: "120px", marginTop: "64px" }}>
            <Box className="translation-notification-container" sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 184px)' }}>
                <Box>
                    <Typography variant="h6" className="translation-notification-title">
                        {t('Send Notification for Translation')}
                    </Typography>
                    <Grid item xs={12} sm={6} md={2.4} gap={2} mt={2} mb={2} spacing={2} display={"flex"} flexDirection={"row"} justifyContent={"space-between"}>
                         <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                            
                           
                        <Grid item xs={12} sm={6} md={2.4} display={"flex"}>
                        <DatePicker
                                label="Modification from date"
                                value={fromDate}
                                onChange={handleFromDateChange}
                                maxDate={toDate}
                                format="DD-MM-YYYY"
                                height="100%"
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: "small",
                                        InputLabelProps: {
                                            shrink: true,
                                            sx: {
                                                backgroundColor: "white",
                                                paddingX: '4px'
                                            },
                                        },
                                        sx: {
                                            '& .MuiOutlinedInput-notchedoutlined': {
                                                top: 0
                                            },
                                            '& .MuiInputLabel-outlined': {
                                                zIndex: 1
                                            },
                                            '& .MuiOutlinedInput-root':{
                                                height:"100%"
                                            }
                                        },
                                        height:"100%"

                                    },
                                }}
                            />
                           

                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4} display={"flex"}>

                        <DatePicker
                                label={t("Modification to date")}
                                value={toDate}
                                onChange={handleToDateChange}
                                format="DD-MM-YYYY"
                                minDate={fromDate}

                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: "small",
                                        InputLabelProps: {
                                            shrink: true,
                                            sx: {
                                                backgroundColor: "white",
                                                paddingX: '4px'
                                            },
                                        },
                                        sx: {
                                            '& .MuiOutlinedInput-notchedoutlined': {
                                                top: 0
                                            },
                                            '& .MuiInputLabel-outlined': {
                                                zIndex: 1
                                            },
                                            '& .MuiOutlinedInput-root':{
                                                height:"100%"
                                            }
                                        },
                                        height:"100%"

                                    },
                                }}
                            />
                            </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>

                            <TextField
                                placeholder={t("Type here")}
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                className="translation-notification-input"
                            />
                            </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>

                            <DatePicker
                                label={t("Sent Notification From Date")}
                                value={notificationFromDate}
                                onChange={handleNotificationFromDateChange}
                                inputFormat="DD-MM-YYYY"
                                maxDate={notificationToDate}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        className="translation-notification-input"                                      
                                    />
                                )}
                            />
                            </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>

                            <DatePicker
                                label={t("Sent Notification To Date")}
                                value={notificationToDate}
                                onChange={handleNotificationToDateChange}
                                inputFormat="DD-MM-YYYY"
                                minDate={notificationFromDate}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        className="translation-notification-input"
                                    />
                                )}
                            />
                            </Grid>
                        </LocalizationProvider>

                        </Grid>

                    <Box sx={{ flex: 1, overflow: 'hidden' }}>
                        <TableContainer 
                            component={Paper} 
                            className="translation-notification-table"
                        >
                            <Table>
                                <TableHead>
                                    <TableRow className="translation-notification-header">
                                        <TableCell className="translation-notification-header-cell">{t("Code (Answer/Question)")}</TableCell>
                                        <TableCell className="translation-notification-header-cell">{t("Original Text")}</TableCell>
                                        <TableCell className="translation-notification-header-cell">{t("Added/Updated Text")}</TableCell>
                                        <TableCell className="translation-notification-header-cell">{t("Date & Time of Modification")}</TableCell>
                                        {countries.map(country => (
                                            <TableCell
                                                key={country.countryCode}
                                                className="translation-notification-header-cell"
                                            >
                                                <Box className="translation-notification-header-checkbox-container">
                                                    <Checkbox
                                                        checked={bulkSelections[country.countryCode] || false}
                                                        onChange={() => handleBulkSelection(country.countryCode)}
                                                        disabled={read_only_user}
                                                        size="small"
                                                        className="translation-notification-header-checkbox"
                                                        sx={{
                                                            padding: 0,
                                                            '& .MuiSvgIcon-root': {
                                                                backgroundColor: 'white',
                                                                borderRadius: '4px',
                                                                width: '20px',
                                                                height: '20px',
                                                                border: '1px solid #E0E0E0' // Changed to grey border
                                                            },
                                                            '&:hover': { 
                                                                backgroundColor: 'transparent'
                                                            },
                                                            '&.Mui-checked': {
                                                                '& .MuiSvgIcon-root': {
                                                                    backgroundColor: 'white',
                                                                    color: '#2196f3',
                                                                    borderRadius: '4px'
                                                                }
                                                            },
                                                            '& .MuiTouchRipple-root': {
                                                                display: 'none'
                                                            },
                                                           '&.Mui-disabled.Mui-checked': {
                                                            color: '#112A63',
                                                            opacity:0.6
                                                        }
                                                        }}
                                                    />
                                                    <span style={{ color: 'white', marginLeft: '2px' }}>{country.countryName}</span>
                                                </Box>
                                            </TableCell>
                                        ))}
                                        <TableCell className="translation-notification-header-cell">
                                            <Box className="translation-notification-header-checkbox-container">
                                                <Checkbox
                                                    checked={tableData.every(row => 
                                                        countries.every(country => 
                                                            !row.endMarkets[country.countryCode] || 
                                                            selectedItems[row.translationHistoryId]?.[country.countryCode]
                                                        )
                                                    )}
                                                    onChange={handleBulkSelectAll}
                                                    disabled={read_only_user}
                                                    size="small"
                                                    className="translation-notification-header-checkbox"
                                                    sx={{
                                                        padding: 0,
                                                        '& .MuiSvgIcon-root': {
                                                            backgroundColor: 'white',
                                                            borderRadius: '4px',
                                                            width: '20px',
                                                            height: '20px',
                                                            border: '1px solid #E0E0E0'
                                                        },
                                                        '&.Mui-checked': {
                                                            '& .MuiSvgIcon-root': {
                                                                backgroundColor: 'white',
                                                                color: '#2196f3',
                                                                borderRadius: '4px'
                                                            }
                                                        },
                                                        '&.MuiCheckbox-root': {
                                                            color: '#2196f3'
                                                        },
                                                        '&.Mui-disabled.Mui-checked': {
                                                            color: '#112A63',
                                                            opacity:0.6
                                                        }
                                                    }}
                                                />
                                                <span style={{ color: 'white', marginLeft: '2px' }}>{t("Select All")}</span>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Array.isArray(tableData) && tableData.length > 0 ? (
                                        tableData.map((row, index) => (
                                            <TableRow key={row.translationHistoryId} className="translation-notification-row">
                                                <TableCell className="translation-notification-cell">
                                                    <Tooltip
                                                        title={row.code}
                                                        placement="top"
                                                        classes={{ tooltip: 'translation-notification-tooltip' }}
                                                    >
                                                        <div className="translation-notification-truncate">{row.code}</div>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell className="translation-notification-cell">
                                                    <Tooltip
                                                        title={row.originalText }
                                                        placement="top"
                                                        classes={{ tooltip: 'translation-notification-tooltip' }}
                                                    >
                                                        <div className="translation-notification-truncate">{row.originalText }</div>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell className="translation-notification-cell">
                                                    <Tooltip
                                                        title={row.updatedText}
                                                        placement="top"
                                                        classes={{ tooltip: 'translation-notification-tooltip' }}
                                                    >
                                                        <div className="translation-notification-truncate">{row.updatedText}</div>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell className="translation-notification-cell">
                                                    {dayjs(row.textChangeDate).format('DD/MM/YYYY HH:mm')}
                                                </TableCell>
                                                {countries.map(country => (
                                                    <TableCell
                                                        key={country.countryCode}
                                                        className="translation-notification-checkbox-cell"
                                                    >
                                                        <div 
                                                            className="translation-notification-checkbox-wrapper"
                                                            onClick={() => {
                                                                if (!row.endMarkets[country.countryCode] && !read_only_user) {
                                                                    setMessage(t("This record is not applicable to the selected market"));
                                                                    setSeverity("error");
                                                                    setOpenAlert(true);
                                                                }
                                                            }}
                                                        >
                                                            <Checkbox
                                                                checked={
                                                                    selectedItems[row.translationHistoryId]?.[country.countryCode] ||
                                                                    row.endMarkets[country.countryCode]?.isNotificationSent
                                                                }
                                                                onChange={() => handleCheckboxClick(row, country, !row.endMarkets[country.countryCode])}
                                                                disabled={read_only_user||!row.endMarkets[country.countryCode]}
                                                                size="small"
                                                                sx={{
                                                                    '&.Mui-checked': {
                                                                        color: selectedItems[row.translationHistoryId]?.[country.countryCode]
                                                                            // ? '#112A63'
                                                                           ?'#2196f3'
                                                                            : 'rgba(0, 0, 0, 0.38)'
                                                                    },
                                                                    '&.MuiCheckbox-root': {
                                                                        color: (!selectedItems[row.translationHistoryId]?.[country.countryCode] && 
                                                                               row.endMarkets[country.countryCode]?.isNotificationSent)
                                                                            ? 'rgba(0, 0, 0, 0.38)'
                                                                            // : '#112A63'
                                                                           :'#2196f3'
                                                                    },
                                                                    '&.Mui-disabled': {
                                                                        color: 'rgba(0, 0, 0, 0.26)',
                                                                        cursor: 'not-allowed'
                                                                    },
                                                                    '&.Mui-disabled.Mui-checked': {
                                                            color: '#112A63',
                                                            opacity:0.6
                                                        }
                                                                }}
                                                            />
                                                        </div>
                                                    </TableCell>
                                                ))}
                                                <TableCell className="translation-notification-checkbox-cell">
                                                    <div className="translation-notification-checkbox-wrapper">
                                                        <Checkbox
                                                            checked={
                                                                hasEnabledCheckboxes(row) && 
                                                                countries.every(country => 
                                                                    !row.endMarkets[country.countryCode] || 
                                                                    selectedItems[row.translationHistoryId]?.[country.countryCode]
                                                                )
                                                            }
                                                            onChange={() => handleSelectAllForRow(row)}
                                                            disabled={read_only_user||!hasEnabledCheckboxes(row)}
                                                            size="small"
                                                            sx={{
                                                                '&.Mui-checked': {
                                                                    // color: '#112A63'
                                                                    color:'#2196f3'
 

                                                                },
                                                                '&.MuiCheckbox-root': {
                                                                    // color: '#112A63'
                                                                    color:'#2196f3'
                                                                },
                                                                '&.Mui-disabled': {
                                                                    color: 'rgba(0, 0, 0, 0.26)',
                                                                    cursor: 'not-allowed'
                                                                },
                                                                '&.Mui-disabled.Mui-checked': {
                                                            color: '#112A63',
                                                            opacity:0.6
                                                        }
                                                            }}
                                                        />
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell
                                                colSpan={4 + (countries?.length || 0)}
                                                align="center"
                                                className="translation-notification-cell"
                                            >
                                                {t("No data available")}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Box>

                <Box sx={{ 
                    position: 'sticky', 
                    bottom: 0, 
                    backgroundColor: 'white',
                    padding: '16px 0',
                    borderTop: '1px solid #E0E0E0',
                    display: 'flex', 
                    justifyContent: 'center',
                    marginTop: 'auto'
                }}>
                    <Button
                        variant="contained"
                        className="translation-notification-button"
                        onClick={handleSendNotification}
                        disabled={isLoading|| read_only_user}
                        sx={{
                      '&.Mui-disabled': {
                        backgroundColor: "#102A62", 
                        color: "#ffffff",    
                        opacity: 0.6,          
                        },
                       }}
                    >
                        {t("SEND NOTIFICATION")}
                        {isLoading && <CircularProgress size={20} sx={{ color: 'white', marginLeft: 1 }} />}
                    </Button>
                </Box>
            </Box>

            {/* Confirmation Modal */}
            <SurityModal
                open={openSurityModal}
                onClose={() => setOpenSurityModal(false)}
                onNext={handleConfirmSend}
                message={t("Do you really want to send the notification to the selected end markets?")}
                iconName={iconCache.warning?.src || WarningIcon}
                loading={isLoading}
            />

            {/* Success Modal */}
            <SurityModal
                open={openSuccessModal}
                onClose={handleSuccessModalClose}
                message={t("Notification has been successfully sent to the selected End Market's local users")}
                iconName={iconCache.success?.src || SuccessIcon}
                singleButton={true}
                loading={false}
            />
        </Layout>
    );
};

export default TranslationNotification;